import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Loader2, LogOut, Save, Eye, CheckCircle, AlertCircle, 
  ArrowLeft, Mail, FileText, RefreshCw 
} from 'lucide-react';

const EmailTemplates = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editedTemplate, setEditedTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [message, setMessage] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/email-templates');
      setTemplates(response.data);
      if (response.data.length > 0 && !selectedTemplate) {
        setSelectedTemplate(response.data[0]);
        setEditedTemplate(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      if (error.response?.status === 404 || templates.length === 0) {
        setMessage({ 
          type: 'info', 
          text: 'No templates found. Click "Initialize Templates" to create default templates.' 
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeTemplates = async () => {
    try {
      setInitializing(true);
      setMessage(null);
      await api.post('/admin/email-templates/initialize');
      setMessage({ type: 'success', text: 'Default templates initialized successfully!' });
      await fetchTemplates();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to initialize templates' 
      });
    } finally {
      setInitializing(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setEditedTemplate({ ...template });
    setShowPreview(false);
    setMessage(null);
  };

  const handleInputChange = (field, value) => {
    setEditedTemplate(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      
      await api.put(`/admin/email-templates/${editedTemplate.template_type}`, {
        subject: editedTemplate.subject,
        html_content: editedTemplate.html_content,
        is_active: editedTemplate.is_active
      });
      
      setMessage({ type: 'success', text: 'Template saved successfully!' });
      await fetchTemplates();
      
      // Update selected template
      const updated = templates.find(t => t.template_type === editedTemplate.template_type);
      if (updated) {
        setSelectedTemplate(updated);
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save template' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      setMessage({ type: 'error', text: 'Please enter an email address' });
      return;
    }

    try {
      setSendingTest(true);
      setMessage(null);

      await api.post(
        `/admin/email-templates/send-test?template_type=${editedTemplate.template_type}&test_email=${testEmail}`
      );

      setMessage({ 
        type: 'success', 
        text: `Test email sent to ${testEmail}! Check your inbox.` 
      });
      setTestEmail('');
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to send test email' 
      });
    } finally {
      setSendingTest(false);
    }
  };

  const handlePreview = async () => {
    try {
      setMessage(null);
      
      // Create test variables
      const testVariables = {};
      editedTemplate.variables.forEach(variable => {
        if (variable === 'user_name') testVariables[variable] = 'John Doe';
        else if (variable === 'reset_link') testVariables[variable] = 'https://example.com/reset?token=abc123';
        else if (variable === 'verify_link') testVariables[variable] = 'https://example.com/verify?token=xyz789';
        else if (variable === 'company_name') testVariables[variable] = 'Acme Inc.';
        else if (variable === 'logo_url') testVariables[variable] = 'https://slicksignature.com/wp-content/uploads/2022/05/SlickSignature.com-Logo_Horizontal-White.png';
        else if (variable === 'plan_name') testVariables[variable] = 'Business';
        else if (variable === 'trial_end_date') testVariables[variable] = 'January 15, 2025';
        else if (variable === 'dashboard_link') testVariables[variable] = `${window.location.origin}/settings`;
        else if (variable === 'days_remaining') testVariables[variable] = '3';
        else if (variable === 'monthly_price') testVariables[variable] = '59';
        else if (variable === 'billing_link') testVariables[variable] = `${window.location.origin}/settings?tab=billing`;
        else if (variable === 'amount') testVariables[variable] = '59.00';
        else if (variable === 'next_billing_date') testVariables[variable] = 'February 1, 2025';
        else if (variable === 'invoice_link') testVariables[variable] = 'https://example.com/invoice';
        else if (variable === 'update_payment_link') testVariables[variable] = `${window.location.origin}/settings?tab=billing`;
        else if (variable === 'access_end_date') testVariables[variable] = 'January 31, 2025';
        else if (variable === 'grace_period_end') testVariables[variable] = 'March 1, 2025';
        else if (variable === 'reactivate_link') testVariables[variable] = `${window.location.origin}/settings?tab=billing`;
        else testVariables[variable] = `[${variable}]`;
      });
      
      const response = await api.post(
        `/admin/email-templates/preview?template_type=${editedTemplate.template_type}`,
        testVariables
      );
      
      setPreviewHtml(response.data.html_content);
      setShowPreview(true);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to generate preview' 
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleBackToAdmin = () => {
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black text-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://cdn.slicksignature.com/landing/slicksignature-logo.png" 
                alt="SlickSignature"
                className="h-12"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<h1 class="text-2xl font-bold">SlickSignature</h1>';
                }}
              />
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-300">
                {user?.email}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-white hover:bg-gray-800"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="outline"
          onClick={handleBackToAdmin}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin Portal
        </Button>

        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Email Templates</h1>
            <p className="text-gray-600 mt-1">Customize email content sent to users</p>
          </div>
          <Button onClick={handleInitializeTemplates} disabled={initializing}>
            {initializing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Initializing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                {templates.length === 0 ? 'Initialize Templates' : 'Add Missing Templates'}
              </>
            )}
          </Button>
        </div>

        {message && (
          <Alert className={`mb-6 ${
            message.type === 'success' ? 'bg-green-50 border-green-200' : 
            message.type === 'info' ? 'bg-blue-50 border-blue-200' :
            'bg-red-50 border-red-200'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <AlertCircle className={`h-4 w-4 ${
                message.type === 'info' ? 'text-blue-600' : 'text-red-600'
              }`} />
            )}
            <AlertDescription className={
              message.type === 'success' ? 'text-green-800' :
              message.type === 'info' ? 'text-blue-800' :
              'text-red-800'
            }>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        {templates.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Email Templates</h3>
              <p className="text-gray-600 mb-6">Click the button above to initialize default email templates</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Template List */}
            <div className="space-y-3">
              <h3 className="font-semibold text-sm text-gray-500 uppercase tracking-wide">Templates</h3>
              {templates.map((template) => (
                <Card
                  key={template.template_type}
                  className={`cursor-pointer transition-all ${
                    selectedTemplate?.template_type === template.template_type
                      ? 'ring-2 ring-primary'
                      : 'hover:shadow-md'
                  }`}
                  onClick={() => handleSelectTemplate(template)}
                >
                  <CardHeader className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <Mail className="h-5 w-5 text-primary mt-1" />
                        <div>
                          <CardTitle className="text-base">{template.name}</CardTitle>
                          <CardDescription className="text-xs mt-1">
                            {template.description}
                          </CardDescription>
                        </div>
                      </div>
                      {template.is_active && (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      )}
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>

            {/* Template Editor */}
            <div className="lg:col-span-2">
              {editedTemplate && (
                <Tabs defaultValue="edit" value={showPreview ? 'preview' : 'edit'} onValueChange={(val) => {
                  if (val === 'preview' && !showPreview) {
                    handlePreview();
                  } else {
                    setShowPreview(val === 'preview');
                  }
                }}>
                  <TabsList className="grid w-full grid-cols-2 mb-4">
                    <TabsTrigger value="edit">Edit Template</TabsTrigger>
                    <TabsTrigger value="preview">Preview</TabsTrigger>
                  </TabsList>

                  <TabsContent value="edit">
                    <Card>
                      <CardHeader>
                        <CardTitle>{editedTemplate.name}</CardTitle>
                        <CardDescription>{editedTemplate.description}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Subject */}
                        <div>
                          <Label htmlFor="subject">Email Subject</Label>
                          <Input
                            id="subject"
                            value={editedTemplate.subject}
                            onChange={(e) => handleInputChange('subject', e.target.value)}
                          />
                        </div>

                        {/* HTML Content */}
                        <div>
                          <Label htmlFor="html_content">HTML Content</Label>
                          <Textarea
                            id="html_content"
                            value={editedTemplate.html_content}
                            onChange={(e) => handleInputChange('html_content', e.target.value)}
                            rows={20}
                            className="font-mono text-sm"
                          />
                        </div>

                        {/* Available Variables */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-blue-900 mb-2">Available Variables:</h4>
                          <div className="flex flex-wrap gap-2">
                            {editedTemplate.variables.map((variable) => (
                              <code key={variable} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                {`{{${variable}}}`}
                              </code>
                            ))}
                          </div>
                          <p className="text-xs text-blue-700 mt-2">
                            Use these variables in your email content. They will be replaced with actual values when emails are sent.
                          </p>
                        </div>

                        {/* Actions */}
                        <div className="flex space-x-3">
                          <Button onClick={handleSave} disabled={saving}>
                            {saving ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save className="h-4 w-4 mr-2" />
                                Save Template
                              </>
                            )}
                          </Button>
                          <Button variant="outline" onClick={handlePreview}>
                            <Eye className="h-4 w-4 mr-2" />
                            Preview
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="preview">
                    <Card>
                      <CardHeader>
                        <CardTitle>Email Preview</CardTitle>
                        <CardDescription>Preview with sample data</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {previewHtml ? (
                          <>
                            {/* Send Test Email Section */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <h4 className="text-sm font-semibold text-blue-900 mb-3">Send Test Email</h4>
                              <p className="text-xs text-blue-700 mb-3">
                                Send this email to yourself to see how it looks in your email client
                              </p>
                              <div className="flex space-x-3">
                                <Input
                                  type="email"
                                  placeholder="your-email@example.com"
                                  value={testEmail}
                                  onChange={(e) => setTestEmail(e.target.value)}
                                  className="flex-1"
                                />
                                <Button
                                  onClick={handleSendTestEmail}
                                  disabled={sendingTest || !testEmail}
                                >
                                  {sendingTest ? (
                                    <>
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                      Sending...
                                    </>
                                  ) : (
                                    <>
                                      <Mail className="h-4 w-4 mr-2" />
                                      Send Test
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>

                            {/* Preview */}
                            <div className="border rounded-lg p-4 bg-white">
                              <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-12 text-gray-500">
                            <Eye className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                            <p>Click "Preview" to see how your email will look</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default EmailTemplates;
